<template>
  <div class="home">
    <HomeBanner :heroContent="displayHeroContent" />
    
    <section class="section features-section">
      <div class="container">
        <h2 class="section-title">{{ homeContent.features_title || '皆の英語教室で学ぶ、6つのポイント' }}</h2>
        <div v-if="isLoadingHomeContent" class="loading-state">
          読み込み中...
        </div>
        <div v-else class="features-grid">
          <div v-for="(feature, index) in displayFeatures" :key="index" class="feature-item">
            <div class="feature-icon">
              <i :class="feature.icon"></i>
            </div>
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>
    
    <section class="section courses-section">
      <div class="container">
        <h2 class="section-title">{{ homeContent.courses_title || 'コースのご案内' }}</h2>
        <div v-if="isLoadingHomeContent" class="loading-state">
          読み込み中...
        </div>
        <div v-else class="courses-grid">
          <CourseCard
            v-for="(course, index) in displayCourses"
            :key="index"
            :title="course.title"
            :schedule="course.schedule"
            :description="course.description"
            :colorClass="course.colorClass"
            :image="course.image"
          />
        </div>
        <div class="text-center">
          <router-link to="/courses" class="btn">{{ homeContent.courses_button || 'もっと詳しく' }} &rarr;</router-link>
        </div>
      </div>
    </section>
    
    <section class="section feedback-notices-section">
      <div class="container">
        <div class="two-column-grid">
          <!-- お知らせ部分 -->
          <div class="notices-column">
            <h2 class="section-title">お知らせ</h2>
            <div class="notices-cards home-notices-cards">
              <div class="notice-card" v-for="notice in notices" :key="notice.id">
                <div v-if="notice.imageUrl" class="notice-image-container-home">
                   <img :src="notice.imageUrl" alt="" class="notice-image-element-home">
                </div>
                <div class="notice-text-content-home">
                  <div class="notice-header-home">
                     <span class="notice-card-date">{{ formatDate(notice.date) }}</span>
                     <span v-if="notice.isImportant" class="notice-badge important notice-badge-home">重要</span>
                  </div>
                  <div class="notice-card-title">{{ notice.title }}</div>
                  <div class="notice-content-wrapper-home">
                    <div class="notice-content-home" v-html="notice.content"></div>
                  </div>
                  <button @click="openNoticeModal(notice)" class="read-more-btn-home">続きを読む</button>
                </div>
              </div>
              <div class="text-center mt-4">
                <router-link to="/notices" class="btn btn-sm">すべてのお知らせを見る</router-link>
              </div>
            </div>
          </div>
          
          <!-- 生徒様の声部分 -->
          <div class="testimonials-column">
            <h2 class="section-title">生徒様の声</h2>
            <div class="testimonials-carousel">
              <div v-if="isLoadingHomeContent" class="loading-state">
                読み込み中...
              </div>
              <div v-else class="testimonials-wrapper" :style="{ transform: `translateX(-${currentSlide * 100}%)` }">
                <div v-for="(testimonial, index) in displayTestimonials" :key="index" class="testimonial-card">
                  <div class="testimonial-image">
                    <img :src="getTestimonialImageUrl(testimonial.image)" :alt="testimonial.name">
                  </div>
                  <div class="testimonial-content">
                    <h3>{{ testimonial.name }}</h3>
                    <h4>{{ testimonial.course }}</h4>
                    <p>{{ testimonial.content }}</p>
                  </div>
                </div>
              </div>
              
              <!-- 左右箭头控制 -->
              <div class="carousel-arrows">
                <button class="carousel-arrow prev" @click="prevSlide" aria-label="前のスライド">
                  <i class="fas fa-chevron-left"></i>
                </button>
                <button class="carousel-arrow next" @click="nextSlide" aria-label="次のスライド">
                  <i class="fas fa-chevron-right"></i>
                </button>
              </div>
              
              <!-- 导航控制 -->
              <div class="carousel-controls">
                <span 
                  v-for="(testimonial, index) in displayTestimonials" 
                  :key="index"
                  class="carousel-dot" 
                  :class="{ active: currentSlide === index }" 
                  @click="goToSlide(index)"
                ></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <section class="section cta-section">
      <div class="container">
        <div class="cta-content">
          <h2 class="cta-title">{{ homeContent.cta_title || 'まずはお気軽に体験レッスンから！' }}</h2>
          <p class="cta-text">{{ homeContent.cta_text || '4レッスン 4950円〜' }}</p>
          <router-link to="/contact" class="btn btn-large">{{ homeContent.cta_button || 'お問い合わせ' }}</router-link>
        </div>
      </div>
    </section>

    <!-- Notice Detail Modal -->
    <div v-if="isNoticeModalOpen" class="modal-overlay" @click.self="closeNoticeModal">
      <div class="modal-content">
        <button class="modal-close-btn" @click="closeNoticeModal">&times;</button>
        <div v-if="selectedNotice">
          <h2 class="modal-title">{{ selectedNotice.title }}</h2>
          <div class="modal-meta">
            <span class="modal-date">{{ formatDate(selectedNotice.date) }}</span>
            <span v-if="selectedNotice.isImportant" class="notice-badge important modal-badge">重要</span>
          </div>
          <div v-if="selectedNotice.imageUrl" class="modal-image">
            <img :src="selectedNotice.imageUrl" alt="お知らせ画像">
          </div>
          <div class="modal-body" v-html="selectedNotice.content"></div>
        </div>
      </div>
    </div>

    <!-- Floating AI Chat Button with Avatar -->
    <div class="fab-container" @click="openAIChatModal" role="button" aria-label="AI英先生を開く">
      <img src="@/assets/ai.png" alt="AI英先生 Avatar" class="fab-avatar">
      <button class="fab-ai-chat" aria-hidden="true">
        <span class="fab-text">AI英先生</span>
      </button>
    </div>

    <!-- AI Chat Modal (Handles auth check internally now) -->
    <AIChatModal :is-open="isAIChatModalOpen" @close="closeAIChatModal" />

  </div>
</template>

<script>
import HomeBanner from '@/components/HomeBanner.vue'
import CourseCard from '@/components/CourseCard.vue'
import AIChatModal from '@/components/AIChatModal.vue'
import { ref, onMounted, computed } from 'vue'

export default {
  name: 'HomePage',
  components: {
    HomeBanner,
    CourseCard,
    AIChatModal
  },
  setup() {
    const notices = ref([]);
    const currentSlide = ref(0);
    const touchStartX = ref(0);
    const touchEndX = ref(0);
    const autoplayInterval = ref(null);

    // Notice Modal state and logic
    const isNoticeModalOpen = ref(false);
    const selectedNotice = ref(null);

    // AI Chat Modal state and logic
    const isAIChatModalOpen = ref(false);

    // Home content state
    const homeContent = ref({
      features_title: '',
      features: [],
      testimonials: [],
      courses_title: '',
      courses: [],
      courses_button: '',
      hero_title: '',
      hero_subtitle: '',
      hero_text: '',
      hero_button1: '',
      hero_button2: '',
      hero_image: '',
      cta_title: '',
      cta_text: '',
      cta_button: ''
    });
    const isLoadingHomeContent = ref(false);

    // Default features if API fails
    const defaultFeatures = [
      { icon: 'fas fa-graduation-cap', title: '国際資格を持つ講師が指導', description: 'ITTT認定英語講師、シンガポール国立大学 免疫学講師' },
      { icon: 'fas fa-users', title: '老若男女みんなが楽しめる', description: 'どんな年齢の方も楽しめる、実践的な学び' },
      { icon: 'fas fa-user-graduate', title: '一人一人に合ったカリキュラム', description: '子どもも、お年寄りも、それぞれに合った指導' },
      { icon: 'fas fa-award', title: '英検対策もバッチリ！', description: '段階別の実力に合わせた指導で合格への近道' },
      { icon: 'fas fa-gamepad', title: '楽しく、遊びながら学ぶ', description: 'ゲーム、ロールプレイ、ストーリーテリング' },
      { icon: 'fas fa-subway', title: '駅チカ！アクセスよし', description: '御堂筋線・谷町線どちらからも！中崎町駅から徒歩2分' }
    ];

    // Default testimonials if API fails
    const defaultTestimonials = [
      {
        name: '清水さん（保護者様）',
        course: '子供向けクラス受講',
        content: '子どもが英語を話すことに自信を持つようになりました。先生もとても親切です。',
        image: 'q1.jpg'
      },
      {
        name: '綿貫さん',
        course: 'シニア向けクラス受講',
        content: 'この教室の雰囲気が大好きです。英語を学ぶのがこんなに楽しいなんて！',
        image: 'q2.jpg'
      }
    ];

    // Default courses if API fails
    const defaultCourses = [
      {
        title: '子ども向け英語クラス',
        schedule: '毎週木曜日 16:00 - 17:00 / 17:00 - 18:00',
        description: 'みんなで物語やゲームを通して楽しく学ぶ',
        colorClass: 'pink',
        image: 'kids.png'
      },
      {
        title: 'シニア向け英会話',
        schedule: '毎週木曜日 10:00 - 11:00',
        description: '自信を持って英語を話せるように、楽しく学びます',
        colorClass: 'green',
        image: 'senior.png'
      },
      {
        title: '英検対策クラス',
        schedule: '毎週木曜日 16:00 - 17:00 / 17:00 - 18:00',
        description: 'ステップごとの指導で合格へ導く',
        colorClass: 'yellow',
        image: 'exam.png'
      }
    ];

    // Computed properties for display data
    const displayFeatures = computed(() => {
      return homeContent.value.features && homeContent.value.features.length > 0 
        ? homeContent.value.features 
        : defaultFeatures;
    });

    const displayTestimonials = computed(() => {
      return homeContent.value.testimonials && homeContent.value.testimonials.length > 0 
        ? homeContent.value.testimonials 
        : defaultTestimonials;
    });

    // Computed for courses
    const displayCourses = computed(() => {
      return homeContent.value.courses && homeContent.value.courses.length > 0 
        ? homeContent.value.courses 
        : defaultCourses;
    });

    // Computed for hero content
    const displayHeroContent = computed(() => {
      return {
        title: homeContent.value.hero_title || '皆の英語教室',
        subtitle: homeContent.value.hero_subtitle || '楽しく、心を動かす英語教室！',
        text: homeContent.value.hero_text || '自信を持って英語を学ぶ。実践的なレッスンで英語力アップ！',
        button1: homeContent.value.hero_button1 || 'コースを見る',
        button2: homeContent.value.hero_button2 || 'お問い合わせ',
        image: homeContent.value.hero_image || ''
      };
    });

    // Load home page content from API
    const fetchHomeContent = async () => {
      isLoadingHomeContent.value = true;
      
      try {
        const response = await fetch('https://enen.saitsuki.uk/api/page-content/home');
        
        if (response.ok) {
          const data = await response.json();
          const content = data.data;
          
          homeContent.value = {
            features_title: content.home_features_title || '皆の英語教室で学ぶ、6つのポイント',
            features: content.home_features ? JSON.parse(content.home_features) : [],
            testimonials: content.home_testimonials ? JSON.parse(content.home_testimonials) : [],
            courses_title: content.home_courses_title || 'コースのご案内',
            courses: content.home_courses ? JSON.parse(content.home_courses) : [],
            courses_button: content.home_courses_button || 'もっと詳しく',
            hero_title: content.home_hero_title || '',
            hero_subtitle: content.home_hero_subtitle || '',
            hero_text: content.home_hero_text || '',
            hero_button1: content.home_hero_button1 || '',
            hero_button2: content.home_hero_button2 || '',
            hero_image: content.home_hero_image || '',
            cta_title: content.home_cta_title || '',
            cta_text: content.home_cta_text || '',
            cta_button: content.home_cta_button || ''
          };
        } else {
          console.warn('Failed to load home content, using defaults');
        }
      } catch (error) {
        console.error('ホーム内容の読み込みに失敗しました:', error);
      } finally {
        isLoadingHomeContent.value = false;
      }
    };

    // Get testimonial image URL (similar to admin)
    const getTestimonialImageUrl = (imageName) => {
      if (!imageName) return require('@/assets/q1.jpg');
      
      // Check if it's a full URL (uploaded image)
      if (imageName.startsWith('http')) {
        return imageName;
      }
      
      // Default assets
      try {
        return require(`@/assets/${imageName}`);
      } catch {
        return require('@/assets/q1.jpg');
      }
    };

    const openNoticeModal = (notice) => {
      selectedNotice.value = notice;
      isNoticeModalOpen.value = true;
      document.body.style.overflow = 'hidden';
    };

    const closeNoticeModal = () => {
      isNoticeModalOpen.value = false;
      selectedNotice.value = null;
      document.body.style.overflow = '';
    };

    const openAIChatModal = () => {
      isAIChatModalOpen.value = true;
      document.body.style.overflow = 'hidden';
    };

    const closeAIChatModal = () => {
      isAIChatModalOpen.value = false;
      document.body.style.overflow = '';
    };

    const fetchNotices = async () => {
      try {
        // 添加时间戳避免缓存
        const timestamp = new Date().getTime();
        const response = await fetch(`https://enen.saitsuki.uk/api/notices?limit=2&t=${timestamp}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch notices');
        }
        
        const data = await response.json();
        const noticesArray = data.notices || [];
        
        // 添加调试信息
        console.log('首页获取到的通知数量:', noticesArray.length);
        console.log('通知数据:', noticesArray);
        console.log('后端调试信息:', data.debug);
        
        notices.value = noticesArray.map(notice => ({
          id: notice.id,
          title: notice.title,
          content: notice.content,
          date: notice.date,
          isImportant: notice.is_important,
          imageUrl: notice.image_url || ''
        }));
        
        console.log('处理后的通知数量:', notices.value.length);
      } catch (error) {
        console.error('通知获取エラー:', error);
      }
    };

    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    };

    const startAutoplay = () => {
      autoplayInterval.value = setInterval(() => {
        nextSlide();
      }, 5000);
    };

    const stopAutoplay = () => {
      if (autoplayInterval.value) {
        clearInterval(autoplayInterval.value);
      }
    };

    const setupTouchEvents = () => {
      const carousel = document.querySelector('.testimonials-carousel');
      if (!carousel) return;
      
      carousel.addEventListener('touchstart', handleTouchStart, { passive: true });
      carousel.addEventListener('touchend', handleTouchEnd, { passive: true });
    };

    const handleTouchStart = (e) => {
      touchStartX.value = e.changedTouches[0].screenX;
      stopAutoplay();
    };

    const handleTouchEnd = (e) => {
      touchEndX.value = e.changedTouches[0].screenX;
      handleSwipe();
      startAutoplay();
    };

    const handleSwipe = () => {
      const diff = touchStartX.value - touchEndX.value;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          nextSlide();
        } else {
          prevSlide();
        }
      }
    };

    const showSlide = (index) => {
      currentSlide.value = index;
    };

    const nextSlide = () => {
      const slides = displayTestimonials.value;
      if (slides.length === 0) return;
      currentSlide.value = (currentSlide.value + 1) % slides.length;
    };

    const prevSlide = () => {
      const slides = displayTestimonials.value;
      if (slides.length === 0) return;
      currentSlide.value = (currentSlide.value - 1 + slides.length) % slides.length;
    };

    const goToSlide = (index) => {
      showSlide(index);
      stopAutoplay();
      startAutoplay();
    };

    onMounted(() => {
      fetchNotices();
      fetchHomeContent();
      startAutoplay();
      setupTouchEvents();
    });

    return {
      notices,
      currentSlide,
      nextSlide,
      prevSlide,
      goToSlide,
      formatDate,
      isNoticeModalOpen,
      selectedNotice,
      openNoticeModal,
      closeNoticeModal,
      isAIChatModalOpen,
      openAIChatModal,
      closeAIChatModal,

      // Home content
      homeContent,
      isLoadingHomeContent,
      displayFeatures,
      displayTestimonials,
      getTestimonialImageUrl,
      displayCourses,
      displayHeroContent
    };
  }
}
</script>

<style scoped>
.courses-section {
  background-color: var(--light-bg);
}

.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  grid-gap: 25px;
  margin-bottom: 40px;
  grid-auto-rows: 1fr;
}

.text-center {
  text-align: center;
}

.features-section {
  padding: 70px 0;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  grid-gap: 30px;
  margin-top: 40px;
}

.feature-item {
  background-color: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
}

.feature-item:hover {
  transform: translateY(-5px);
}

.feature-icon {
  font-size: 28px;
  margin-bottom: 15px;
  color: white;
  display: inline-block;
  width: 60px;
  height: 60px;
  background-color: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(255, 143, 171, 0.3);
}

.feature-item h3 {
  font-size: 18px;
  margin-bottom: 10px;
  color: #333;
}

.feature-item p {
  color: #666;
  line-height: 1.5;
}

/* 新增的两列布局样式 */
.feedback-notices-section {
  padding: 70px 0;
  background-color: var(--light-bg);
}

.two-column-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 40px;
  align-items: stretch;
}

/* 通知卡片样式 */
.notices-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
  flex-grow: 1;
}

.notice-card {
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  display: flex;
  align-items: stretch;
  overflow: hidden;
}

.notice-card:hover {
  transform: translateY(-3px);
}

/* Home Notice Image Styles (Left Side - Desktop) */
.notice-image-container-home {
    flex: 0 0 150px; /* Fixed width */
    overflow: hidden;
    position: relative;
}

.notice-image-element-home {
    display: block;
    width: 100%;
    height: 100%; /* Fill the stretched container height */
    object-fit: cover; /* Cover the area */
    transition: transform 0.3s ease;
}

.notice-card:hover .notice-image-element-home {
    transform: scale(1.05);
}

/* Container for text content (Right Side - Desktop) */
.notice-text-content-home {
    flex: 1;
    padding: 15px 20px;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.notice-header-home {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.notice-card-date {
  color: var(--primary-color);
  font-weight: 500;
  font-size: 13px;
}

.notice-badge-home {
    /* Adjust if needed */
}

.notice-card-title {
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  font-size: 16px;
}

.notice-content-wrapper-home {
  overflow: hidden;
  margin-bottom: 10px;
  flex-grow: 1; /* Allow content to push button down */
}

.notice-content-home {
  color: #666;
  line-height: 1.6;
  font-size: 14px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3; /* Keep it at 3 lines for now */
  overflow: hidden;
  text-overflow: ellipsis;
}

.read-more-btn-home {
  background: none;
  border: none;
  color: var(--primary-color);
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-top: auto; /* Push to bottom */
  align-self: flex-start;
  font-size: 14px;
}

.read-more-btn-home:hover {
  text-decoration: underline;
}

/* Adjustments for when image is missing */
.notice-card:not(:has(.notice-image-container-home)) .notice-text-content-home {
    padding: 20px;
}

/* Responsive adjustments (Mobile) */
@media (max-width: 768px) {
   .notice-card {
       flex-direction: column;
       align-items: normal;
       border-radius: 15px; /* Keep card radius */
       overflow: hidden; /* Clip content */
       padding: 0; /* No padding on card */
   }
   .notice-image-container-home {
       flex-basis: auto;
       width: 100%;
       height: auto;
       aspect-ratio: 16 / 9;
       position: static;
       overflow: hidden;
       margin: 0;
       padding: 0;
       /* Add top border radius to match card */
       border-top-left-radius: 15px;
       border-top-right-radius: 15px;
       /* Ensure no bottom radius if text follows directly */
       border-bottom-left-radius: 0;
       border-bottom-right-radius: 0;
   }
   .notice-image-element-home {
       display: block;
       width: 100%;
       height: 100%;
       object-fit: cover;
       margin: 0;
   }
   .notice-text-content-home {
       flex: none;
       padding: 15px 20px 20px;
   }
   .notice-card:not(:has(.notice-image-container-home)) .notice-text-content-home {
       padding: 20px;
        /* Add radius back if there is no image */
       border-radius: 15px;
   }
   .notice-card-title {
       font-size: 16px;
   }
   .notice-content-home {
        -webkit-line-clamp: 3; /* Adjust line clamp if needed */
   }
   .notice-card:not(:has(.notice-image-container-home)) .notice-text-content-home {
       padding: 20px; /* Ensure consistent padding */
   }
}

/* 学生评价轮播样式 */
.testimonials-carousel {
  position: relative;
  background-color: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  padding: 20px;
  -webkit-user-select: none;
  user-select: none;
  touch-action: pan-y;
  width: 100%;
  box-sizing: border-box;
  min-height: 300px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.testimonials-wrapper {
  display: flex;
  transition: transform 0.6s cubic-bezier(0.45, 0, 0.55, 1);
  width: 100%;
  position: relative;
}

.testimonial-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100%;
  width: 100%;
  opacity: 1;
  padding: 0 10px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.testimonial-image {
  width: 120px;
  height: 120px;
  margin-bottom: 15px;
  position: relative;
  transition: transform 0.5s ease, box-shadow 0.5s ease;
}

.testimonial-card:hover .testimonial-image {
  transform: translateY(-5px);
}

.testimonial-image::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 4px solid var(--accent-color);
  box-sizing: border-box;
  z-index: 2;
  transition: all 0.3s ease;
  pointer-events: none;
}

.testimonial-card:hover .testimonial-image::after {
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  transform: scale(1.05);
}

.testimonial-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.testimonial-card:hover .testimonial-image img {
  transform: scale(1.05);
}

.testimonial-content {
  text-align: center;
  padding: 10px;
  transform: translateY(5px);
  opacity: 0.9;
  transition: transform 0.5s ease, opacity 0.5s ease;
  width: 100%;
  max-width: 280px;
  margin: 0 auto;
}

.testimonial-card:hover .testimonial-content {
  transform: translateY(0);
  opacity: 1;
}

.testimonial-content h3 {
  font-size: 18px;
  margin-bottom: 5px;
  color: #333;
}

.testimonial-content h4 {
  font-size: 14px;
  color: var(--primary-color);
  margin-bottom: 10px;
}

.testimonial-content p {
  color: #555;
  line-height: 1.5;
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
  white-space: normal;
}

/* 轮播箭头控制 */
.carousel-arrows {
  position: absolute;
  width: 100%;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  padding: 0 10px;
  pointer-events: none;
}

.carousel-arrow {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.8);
  border: none;
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  pointer-events: auto;
  opacity: 0;
  transform: scale(0.8);
}

.testimonials-carousel:hover .carousel-arrow {
  opacity: 1;
  transform: scale(1);
}

.carousel-arrow:hover {
  background-color: white;
  color: var(--accent-color);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.carousel-arrow:focus {
  outline: none;
}

/* 轮播导航控制 */
.carousel-controls {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  gap: 10px;
}

.carousel-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #ccc;
  cursor: pointer;
  transition: all 0.3s ease;
}

.carousel-dot.active {
  background-color: var(--primary-color);
  transform: scale(1.2);
  box-shadow: 0 0 10px rgba(255, 143, 171, 0.5);
}

.carousel-dot:hover:not(.active) {
  background-color: #aaa;
  transform: scale(1.1);
}

.cta-section {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  color: white;
  text-align: center;
  padding: 60px 0;
}

.cta-title {
  font-size: 28px;
  margin-bottom: 15px;
}

.cta-text {
  font-size: 20px;
  margin-bottom: 30px;
}

.btn-large {
  padding: 12px 30px;
  font-size: 18px;
  background-color: white;
  color: var(--primary-color);
}

.btn-large:hover {
  background-color: var(--accent-color);
}

.btn-sm {
  padding: 8px 20px;
  font-size: 14px;
}

.mt-4 {
  margin-top: 25px;
}

@media (max-width: 768px) {
  .section {
    padding: 40px 0;
  }
  
  .section-title {
    font-size: 20px;
    margin-bottom: 15px;
  }
  
  .two-column-grid {
    grid-template-columns: 1fr;
    grid-gap: 20px;
    align-items: start;
  }
  
  .testimonial-card {
    flex-direction: column;
  }
  
  .testimonial-image {
    width: 100px;
    height: 100px;
    margin: 0 auto 10px;
  }
  
  .testimonial-content h3 {
    font-size: 16px;
  }
  
  .testimonial-content h4 {
    font-size: 13px;
  }
  
  .testimonial-content p {
    font-size: 14px;
  }
  
  .features-grid {
    grid-gap: 15px;
  }
  
  .feature-item {
    padding: 15px;
  }
  
  .feature-icon {
    width: 45px;
    height: 45px;
    font-size: 20px;
    margin-bottom: 10px;
  }
  
  .feature-item h3 {
    font-size: 16px;
    margin-bottom: 5px;
  }
  
  .feature-item p {
    font-size: 13px;
  }
  
  .courses-grid {
    grid-gap: 15px;
  }
  
  .cta-title {
    font-size: 20px;
  }
  
  .cta-text {
    font-size: 16px;
    margin-bottom: 20px;
  }
  
  .btn-large {
    padding: 10px 24px;
    font-size: 16px;
  }
  
  .notice-card {
    padding: 15px;
  }
  
  .notice-card-date {
    font-size: 13px;
  }
  
  .notice-card-title {
    font-size: 14px;
  }
  
  .testimonial-card:hover .testimonial-image {
    transform: translateY(-3px);
  }
  
  .testimonial-card:hover .testimonial-image img {
    transform: scale(1.03);
  }
  
  .testimonial-card:hover .testimonial-image::after {
    transform: scale(1.03);
  }
  
  .carousel-arrow {
    width: 30px;
    height: 30px;
    font-size: 12px;
  }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
}

.modal-content {
  background-color: #fff;
  padding: 30px 35px;
  border-radius: 10px;
  max-width: 800px;
  width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.2);
}

.modal-close-btn {
  position: absolute;
  top: 10px;
  right: 15px;
  background: none;
  border: none;
  font-size: 30px;
  color: #aaa;
  cursor: pointer;
  line-height: 1;
}

.modal-close-btn:hover {
  color: #333;
}

.modal-title {
  font-size: 24px;
  margin-top: 0;
  margin-bottom: 10px;
  color: #333;
}

.modal-meta {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
}

.modal-date {
  color: #777;
  font-size: 14px;
}

.notice-badge {
  font-size: 11px;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 700;
  text-transform: uppercase;
}

.notice-badge.important {
  background-color: #ffebee;
  color: #c62828;
}

.modal-badge {
  font-size: 12px;
}

.modal-image {
  margin: 20px 0;
  text-align: center;
}

.modal-image img {
  max-width: 100%;
  max-height: 50vh;
  border-radius: 8px;
}

.modal-body {
  color: #555;
  line-height: 1.8;
}

.modal-body p {
  margin-bottom: 1.2em;
}

@media (max-width: 768px) {
  .modal-content {
    padding: 20px 25px;
    max-height: 85vh;
  }
  .modal-title {
    font-size: 20px;
  }
  .modal-close-btn {
    font-size: 26px;
    top: 8px;
    right: 10px;
  }
}

/* Floating AI Chat Button Styles */
.fab-container {
  position: fixed;
  bottom: 25px;
  right: 25px;
  display: flex;
  align-items: center;
  background-color: var(--primary-color);
  border-radius: 30px;
  padding: 8px 15px 8px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  z-index: 1000;
  transition: all 0.3s ease;
  color: white;
}

.fab-container:hover {
  background-color: var(--primary-color-dark);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  transform: translateY(-2px);
}

.fab-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 8px;
  object-fit: cover;
  border: 2px solid white;
}

.fab-ai-chat {
  background-color: transparent;
  color: inherit;
  border: none;
  padding: 0;
  box-shadow: none;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.fab-ai-chat:hover {
  background-color: transparent;
  box-shadow: none;
  transform: none;
}

.fab-text {
  display: inline-block;
}

@media (max-width: 768px) {
  .fab-container {
    bottom: 20px;
    right: 20px;
    padding: 6px 12px 6px 6px;
  }
  .fab-avatar {
    width: 35px;
    height: 35px;
    margin-right: 6px;
  }
  .fab-ai-chat {
    font-size: 14px;
  }
}

/* Loading state styles for home content */
.loading-state {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
  background: #f9f9f9;
  border-radius: 8px;
  margin: 20px 0;
}

.loading-state::before {
  content: '';
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid #ddd;
  border-top: 2px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style> 